import { Drawer, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import React, { Fragment } from 'react'
import { BarChart2, Calendar, CheckSquare, DollarSign, Download, Grid, Home, Package, RefreshCw, User, Users } from 'react-feather'
import { useDispatch } from 'react-redux'
import { NavLink as RouterLink, useLocation } from 'react-router-dom'
import { useAuth } from '../../../authentication/useAuth'
import useIsTablet from '../../../hooks/useIsTablet'
import { setDrawerOpen } from '../../../store/global'
import { tokens } from '../../../theme'
// The release version, set by release/build-release.ps1; a dev server shows 'development'
const version = process.env.REACT_APP_VERSION || 'development';

const sections = [
    {
        header: 'Overview',
        subSections: [
            {
                title: 'Dashboard',
                icon: Home,
                href: '/ims/overview/dashboard'
            }
        ]
    },
    {
        header: 'Operations',
        subSections: [
            {
                title: 'Job Cards',
                icon: Calendar,
                href: '/ims/operations/job-cards'
            },
            {
                title: 'Goods Received Vouchers',
                icon: Download,
                href: '/ims/operations/goods-received-vouchers'
            },
            {
                title: 'Stock Take',
                icon: CheckSquare,
                href: '/ims/operations/stock-take'
            }
        ]
    }, 
    {
        header: 'Reports',
        subSections: [
            // {
            //     title: 'Inventory Movements',
            //     icon: Repeat,
            //     href: '/ims/reports/inventory-movements'
            // },
            // {
            //     title: 'Inventory Levels',
            //     icon: Sliders,
            //     href: '/ims/reports/inventory-levels'
            // },
            {
                title: 'Sales Analysis',
                icon: BarChart2,
                href: '/ims/reports/sales-analysis'
            },
            // {
            //     title: 'Diesel Usage',
            //     icon: Droplet,
            //     href: '/ims/reports/diesel-usage'
            // },
            {
                title: 'Payments Report',
                icon: DollarSign,
                href: '/ims/reports/payments-report'
            }
            // {
            //     title: 'Vehicle Services',
            //     icon: Truck,
            //     href: '/ims/reports/vehicle-services'
            // },

        ]
    },
    {
        header: 'Administration',
        subSections: [
            {
                title: 'Users',
                icon: User,
                href: '/ims/administration/users'
            },
            {
                title: 'Sites',
                icon: Grid,
                href: '/ims/administration/sites'
            },
            {
                title: 'Customers',
                icon: Users,
                href: '/ims/administration/customers'
            },
            {
                title: 'Inventory Items',
                icon: Package,
                href: '/ims/administration/inventory-items'
            },
            {
                title: 'Sync',
                icon: RefreshCw,
                href: '/ims/administration/sync'
            },
            // {
            //     title: 'Vehicles',
            //     icon: Truck,
            //     href: '/ims/administration/vehicles'
            // }
        ]
    }
]

const NavBar = () => {
    const { canView } = useAuth();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const isTablet = useIsTablet();

    const [, , , path] = pathname.split('/')
    const isSectionAuthorized = (section) => {
        let authorized = false;
        section?.subSections?.forEach(element => {
            const module = element.module ?? element.title.toLowerCase().replace(/ /g, "-");
            if(canView(module)) authorized = true;
        });
        return authorized;
    };

    const isSubSectionAuthorized = (subsection) => {
        let authorized = false
        const module = subsection.module ?? subsection.title.replace(/ /g, "-").toLowerCase();
        // canView also knows the default permissions a role has not saved yet
        if (canView(module)) authorized = true;
        return authorized;
    }

    return (
        <Drawer
            open={true}
            variant="permanent"
            anchor='left'
            sx={{
                width: 240,
                height: '100%',
                overflowX: 'hidden',
                [`& .MuiDrawer-paper`]: {
                    width: 240,
                    height: '100%',
                    overflowX: 'hidden',
                    boxSizing: 'border-box',
                    backgroundColor: tokens.navBg,
                    borderRight: `1px solid ${tokens.navDivider}`,
                    padding: '16px 12px'
                }
            }}
        >
            <Stack direction="row" alignItems="center" gap={1.5} px={1} pb={2}>
                <Box
                    component="img"
                    src="/Logo_Blue.png"
                    alt="IMS"
                    sx={{ width: 32, height: 32, borderRadius: '50%', boxShadow: `0 0 0 1px ${tokens.navDivider}` }}
                />
                <Box>
                    <Typography fontWeight={600} fontSize={15} lineHeight="20px" color={tokens.navActiveInk}>IMS</Typography>
                    <Typography fontSize={12} lineHeight="16px" color={tokens.navInkMuted}>Inventory Management</Typography>
                </Box>
            </Stack>
            {sections.map((section, index) => (
                isSectionAuthorized(section) ?
                    <Fragment key={index}>
                        <Typography
                            sx={{
                                px: 1,
                                pt: 2,
                                pb: 0.5,
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: tokens.navInkMuted
                            }}
                        >
                            {section.header}
                        </Typography>
                        {section.subSections.map(sub => {
                            const [, , , subRef] = sub.href.split('/');
                            const Icon = sub.icon;
                            const active = path === subRef;
                            return (
                                isSubSectionAuthorized(sub) ?
                                    <Box
                                        key={sub.href}
                                        component={RouterLink}
                                        to={sub.href}
                                        onClick={() => isTablet ? dispatch(setDrawerOpen()) : null}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            height: 36,
                                            px: 1,
                                            mb: 0.5,
                                            borderRadius: '6px',
                                            textDecoration: 'none',
                                            fontSize: 14,
                                            fontWeight: active ? 600 : 500,
                                            color: active ? tokens.navActiveInk : tokens.navInk,
                                            backgroundColor: active ? tokens.navActive : 'transparent',
                                            '&:hover': { backgroundColor: active ? tokens.navActive : tokens.navHover }
                                        }}
                                    >
                                        <Icon size={16} />
                                        {sub.title}
                                    </Box>
                                : null
                            );
                        })}
                    </Fragment>
                : null
            ))}
            <Typography sx={{ mt: 'auto', px: 1, pt: 2, fontSize: 12, color: tokens.navInkMuted }}>Version {version}</Typography>
        </Drawer>
    )
}

export default NavBar